import { Card } from "@components/cards/card";
import { api } from "@service/api";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaArrowLeft,
  FaChartColumn,
  FaKey,
  FaList,
  FaUserTie,
  FaUsers,
  FaUsersGear,
} from "react-icons/fa6";
import { useNavigate } from "react-router-dom";

interface IUserMetricDTO {
  label: string;
  total: number;
  color: string;
}

interface IUserManagementDashboardDTO {
  totals: {
    customers: number;
    salesmen: number;
    managers: number;
  };
  chart: IUserMetricDTO[];
  lastUpdatedAt: string;
}

const initialUserManagementDashboard: IUserManagementDashboardDTO = {
  totals: {
    customers: 0,
    salesmen: 0,
    managers: 0,
  },
  chart: [],
  lastUpdatedAt: "",
};

const metricCards = [
  {
    key: "customers",
    title: "Total de Clientes",
    icon: FaUsers,
    className: "bg-primary-subtle text-primary",
  },
  {
    key: "salesmen",
    title: "Total de Consultores",
    icon: FaUserTie,
    className: "bg-success-subtle text-success",
  },
  {
    key: "managers",
    title: "Total de Gerentes",
    icon: FaUsersGear,
    className: "bg-warning-subtle text-warning",
  },
] as const;

export const UserManagementDashboard = () => {
  const navigate = useNavigate();
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  const [isLoading, setIsLoading] = useState(true);
  const [dashboard, setDashboard] =
    useState<IUserManagementDashboardDTO>(initialUserManagementDashboard);

  const chartTotal = useMemo(() => {
    return dashboard.chart.reduce((total, item) => total + item.total, 0);
  }, [dashboard.chart]);

  const chartGradient = useMemo(() => {
    if (!chartTotal) return "#e9ecef";

    let accumulated = 0;
    return dashboard.chart
      .map((item) => {
        const start = (accumulated / chartTotal) * 100;
        accumulated += item.total;
        const end = (accumulated / chartTotal) * 100;

        return `${item.color} ${start}% ${end}%`;
      })
      .join(", ");
  }, [chartTotal, dashboard.chart]);

  const getUserManagementDashboard = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await api.get<IUserManagementDashboardDTO>(
        "/customer/management/users/dashboard",
      );

      setDashboard(result.data);
    } catch (error: any) {
      console.error(error);
      pushToastify({
        message:
          error?.message ||
          "Houve um erro ao consultar os dados de gerenciamento de usuários!",
        type: "error",
        theme: "dark",
        pauseOnHover: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [pushToastify]);

  useEffect(() => {
    getUserManagementDashboard();
  }, [getUserManagementDashboard]);

  return (
    <div
      className="container-fluid d-flex flex-column justify-content-start align-items-center"
      style={{
        paddingBottom: "2.5rem",
        paddingTop: "clamp(1rem, 2vw, 2rem)",
        paddingLeft: "clamp(1rem, 5vw, 10rem)",
        paddingRight: "clamp(1rem, 5vw, 10rem)",
      }}
    >
      <div className="col-12">
        <Card className="shadow-sm rounded mb-4 border-0">
          <div className="card-body p-4">
            <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-3">
              <div className="d-flex align-items-center gap-3">
                <div className="rounded-3 d-inline-flex align-items-center justify-content-center p-3 bg-success-subtle text-success">
                  <FaUsersGear size={30} />
                </div>

                <div>
                  <span className="badge rounded-pill text-bg-success mb-2">
                    Gerenciamento
                  </span>
                  <h3 className="fw-bold mb-1">Gerenciamento de Usuários</h3>
                  <p className="text-muted mb-0">
                    Visualize indicadores gerais de clientes, consultores e
                    gerentes da plataforma.
                  </p>
                </div>
              </div>

              <button
                type="button"
                className="btn btn-outline-secondary d-flex align-items-center gap-2"
                onClick={() => navigate("/management")}
              >
                <FaArrowLeft />
                Voltar
              </button>
            </div>
          </div>
        </Card>

        <div className="row g-4">
          <div className="col-12 col-xl-8">
            <div className="row g-4 mb-4">
              {metricCards.map((item) => {
                const Icon = item.icon;
                const total = dashboard.totals[item.key];

                return (
                  <div key={item.key} className="col-12 col-md-4">
                    <Card className="h-100 shadow-sm border-0">
                      <div className="card-body p-4">
                        <div className="d-flex justify-content-between align-items-start gap-3">
                          <div>
                            <p className="text-muted mb-2">{item.title}</p>
                            <h2 className="fw-bold mb-0">
                              {isLoading
                                ? "--"
                                : total.toLocaleString("pt-BR")}
                            </h2>
                          </div>
                          <div
                            className={`rounded-3 d-inline-flex align-items-center justify-content-center p-3 ${item.className}`}
                          >
                            <Icon size={24} />
                          </div>
                        </div>
                      </div>
                    </Card>
                  </div>
                );
              })}
            </div>

            <Card className="shadow-sm border-0">
              <div className="card-header">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h4 className="card-title mb-1">Usuários por Perfil</h4>
                    <p className="card-category mb-0">
                      Distribuição atual por tipo de usuário.
                    </p>
                  </div>
                  <FaChartColumn className="text-primary fs-4" />
                </div>
              </div>
              <div className="card-body p-4">
                <div className="row align-items-center g-4">
                  <div className="col-12 col-lg-5 d-flex justify-content-center">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        width: 220,
                        height: 220,
                        background: isLoading
                          ? "#e9ecef"
                          : `conic-gradient(${chartGradient})`,
                        boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.04)",
                      }}
                    >
                      <div
                        className="rounded-circle bg-white d-flex flex-column align-items-center justify-content-center shadow-sm"
                        style={{ width: 142, height: 142 }}
                      >
                        <span className="text-muted small">Total</span>
                        <strong className="fs-2 text-dark">
                          {isLoading
                            ? "--"
                            : chartTotal.toLocaleString("pt-BR")}
                        </strong>
                        <span className="text-muted small">usuários</span>
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-lg-7">
                    <div
                      className="d-flex w-100 rounded-pill bg-light mb-4"
                      style={{ height: 18, overflow: "hidden" }}
                    >
                      {dashboard.chart.map((item) => {
                        const percent = chartTotal
                          ? (item.total / chartTotal) * 100
                          : 0;

                        return (
                          <div
                            key={item.label}
                            title={`${item.label}: ${percent.toLocaleString(
                              "pt-BR",
                              {
                                maximumFractionDigits: 1,
                              },
                            )}%`}
                            style={{
                              width: isLoading ? "0%" : `${percent}%`,
                              minWidth: item.total > 0 && !isLoading ? 8 : 0,
                              backgroundColor: item.color,
                              transition: "width 0.3s ease",
                            }}
                          />
                        );
                      })}
                    </div>

                    <div className="d-flex flex-column gap-3">
                      {dashboard.chart.map((item) => {
                        const percent = chartTotal
                          ? (item.total / chartTotal) * 100
                          : 0;

                        return (
                          <div
                            key={item.label}
                            className="d-flex justify-content-between align-items-center gap-3"
                          >
                            <div className="d-flex align-items-center gap-2">
                              <span
                                className="rounded-circle d-inline-block"
                                style={{
                                  width: 12,
                                  height: 12,
                                  backgroundColor: item.color,
                                }}
                              />
                              <span className="fw-semibold">{item.label}</span>
                            </div>

                            <div className="text-end">
                              <div className="fw-bold">
                                {isLoading
                                  ? "--"
                                  : item.total.toLocaleString("pt-BR")}
                              </div>
                              <small className="text-muted">
                                {isLoading
                                  ? "--"
                                  : `${percent.toLocaleString("pt-BR", {
                                      maximumFractionDigits: 1,
                                    })}%`}
                              </small>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          <div className="col-12 col-xl-4">
            <Card className="h-100 shadow-sm border-0">
              <div className="card-header">
                <h4 className="card-title mb-1">Ações Rápidas</h4>
                <p className="card-category mb-0">
                  Atalhos para as próximas áreas de usuários.
                </p>
              </div>
              <div className="card-body d-flex flex-column gap-3">
                <button
                  type="button"
                  className="btn btn-primary d-flex align-items-center justify-content-center gap-2"
                  onClick={() => navigate("/management/users/list")}
                  title="Ver relação de usuários."
                >
                  <FaList />
                  Ver Relação de Usuários
                </button>
                <button
                  type="button"
                  className="btn btn-outline-warning d-flex align-items-center justify-content-center gap-2"
                  onClick={() => navigate("/management/users/access-map")}
                  title="Ver mapeamento de acessos."
                >
                  <FaKey />
                  Mapeamento de Acessos
                </button>
                <div className="alert alert-info mb-0 mt-auto">
                  <strong>Atualização:</strong>{" "}
                  {dashboard.lastUpdatedAt
                    ? new Date(dashboard.lastUpdatedAt).toLocaleString("pt-BR")
                    : "--"}
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
